import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment as ENV } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CryptoService {

  private aesKey: CryptoKey = null;
  private keyPair: CryptoKeyPair = null;
  public sessionId: string;
  public isReady: boolean = false;

  // HKDF parameters — must match backend exactly
  private HKDF_SALT = 'ecdh-salt-v1';
  private HKDF_INFO = 'aes-256-gcm-key';

  constructor(private http: HttpClient) {
    this.sessionId = this.generateUUID();
  }

  // ─── Encrypt ────────────────────────────────────────────────────────
  async encrypt(plainObject: any, apiUrl?: string): Promise<{ iv: string; data: string; tag: string }> {
    if (!this.aesKey) {
      throw new Error('CryptoService: AES key not established. Call initSession() first.');
    }

    console.log('CryptoService [ENCRYPT] API:', apiUrl || 'unknown', '| plain payload:', JSON.stringify(plainObject));

    var iv = crypto.getRandomValues(new Uint8Array(12));
    var plainBytes = new TextEncoder().encode(JSON.stringify(plainObject));

    var cipherBuf = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv as any, tagLength: 128 },
      this.aesKey,
      plainBytes as any
    );

    // WebCrypto appends 16-byte auth tag at end of ciphertext buffer
    var allBytes = new Uint8Array(cipherBuf);
    var cipherBytes = allBytes.slice(0, allBytes.length - 16);
    var tagBytes = allBytes.slice(allBytes.length - 16);

    var encrypted = {
      iv: this.bytesToHex(iv),
      data: this.bytesToHex(cipherBytes),
      tag: this.bytesToHex(tagBytes)
    };
    console.log('CryptoService [ENCRYPT] API:', apiUrl || 'unknown', '| encrypted payload:', encrypted);
    return encrypted;
  }

  // ─── Decrypt ────────────────────────────────────────────────────────
  async decrypt(payload: { iv: string; data: string; tag: string }, apiUrl?: string): Promise<any> {
    if (!this.aesKey) {
      throw new Error('CryptoService: AES key not established. Call initSession() first.');
    }

    var ivBytes = this.hexToBytes(payload.iv);
    var cipherBytes = this.hexToBytes(payload.data);
    var tagBytes = this.hexToBytes(payload.tag);

    // WebCrypto expects ciphertext + tag concatenated
    var combined = new Uint8Array(cipherBytes.length + tagBytes.length);
    combined.set(cipherBytes, 0);
    combined.set(tagBytes, cipherBytes.length);

    var plainBuf = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBytes as any, tagLength: 128 },
      this.aesKey,
      combined as any
    );

    var decrypted = JSON.parse(new TextDecoder().decode(plainBuf));
    console.log('CryptoService [DECRYPT] API:', apiUrl || 'unknown', '| encrypted response:', payload);
    console.log('CryptoService [DECRYPT] API:', apiUrl || 'unknown', '| decrypted response:', decrypted);
    return decrypted;
  }

  // ─── Session Init (Key Exchange) ───────────────────────────────────
  async initSession(): Promise<void> {
    try {
      // 1. Generate ECDH key pair (P-256)
      this.keyPair = await crypto.subtle.generateKey(
        { name: 'ECDH', namedCurve: 'P-256' },
        true,
        ['deriveKey', 'deriveBits']
      ) as CryptoKeyPair;

      // 2. Export client public key as raw hex
      var rawPub = await crypto.subtle.exportKey('raw', this.keyPair.publicKey);
      var clientPubHex = this.bytesToHex(new Uint8Array(rawPub));

      // 3. POST key exchange (bypasses encryption interceptor via X-Skip-Encryption header)
      var response = await this.http.post(ENV.keyExchange + '/key-exchange', {
        clientPublicKey: clientPubHex,
        sessionId: this.sessionId
      }, {
        headers: { 'X-Skip-Encryption': 'true' }
      }).toPromise() as any;

      // 4. Import server public key
      var serverPubBytes = this.hexToBytes(response.serverPublicKey);
      var serverPubKey = await crypto.subtle.importKey(
        'raw',
        serverPubBytes as any,
        { name: 'ECDH', namedCurve: 'P-256' },
        false,
        []
      );

      // 5. Derive shared bits (256 bits = 32 bytes)
      var sharedBits = await crypto.subtle.deriveBits(
        { name: 'ECDH', public: serverPubKey } as any,
        this.keyPair.privateKey,
        256
      );

      // 6. Import shared bits as HKDF base key
      var hkdfBaseKey = await crypto.subtle.importKey(
        'raw',
        sharedBits,
        { name: 'HKDF' } as any,
        false,
        ['deriveKey']
      );

      // 7. Derive AES-256-GCM key via HKDF
      this.aesKey = await crypto.subtle.deriveKey(
        {
          name: 'HKDF',
          hash: 'SHA-256',
          salt: new TextEncoder().encode(this.HKDF_SALT),
          info: new TextEncoder().encode(this.HKDF_INFO)
        } as any,
        hkdfBaseKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
      );

      this.isReady = true;
      console.log('CryptoService: E2E encryption session established. Session ID:', this.sessionId);

    } catch (err) {
      this.isReady = false;
      console.error('CryptoService: Key exchange failed', err);
      throw err;
    }
  }

  // ─── Hex Utilities ─────────────────────────────────────────────────
  bytesToHex(bytes: Uint8Array): string {
    var hex = '';
    for (var i = 0; i < bytes.length; i++) {
      var h = bytes[i].toString(16);
      hex += h.length === 1 ? '0' + h : h;
    }
    return hex;
  }

  hexToBytes(hex: string): Uint8Array {
    var bytes = new Uint8Array(hex.length / 2);
    for (var i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes;
  }

  // ─── UUID v4 Generator ─────────────────────────────────────────────
  private generateUUID(): string {
    var d = new Date().getTime();
    var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
    return uuid;
  }
}
